"use client";
import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';

interface PaymentSettings {
  upi_id: string;
  upi_name: string;
  premium_amount: number;
  qr_code_enabled: boolean;
  premium_fee_enabled: boolean;
  whatsapp_number: string;
  whatsapp_enabled: boolean;
}

const PaymentSettingsManager = () => {
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({
    upi_id: 'salon@upi',
    upi_name: 'Fully A/C Salon',
    premium_amount: 100,
    qr_code_enabled: true,
    premium_fee_enabled: true,
    whatsapp_number: '919999999999',
    whatsapp_enabled: true
  });

  const [isEditing, setIsEditing] = useState(false);
  const [tempSettings, setTempSettings] = useState<PaymentSettings>(paymentSettings);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await apiClient.getPaymentSettings();
      // Convert database integers to booleans
      const normalizedSettings = {
        ...settings,
        qr_code_enabled: Boolean(settings.qr_code_enabled),
        premium_fee_enabled: Boolean(settings.premium_fee_enabled),
        whatsapp_enabled: Boolean(settings.whatsapp_enabled)
      };
      setPaymentSettings(normalizedSettings);
      setTempSettings(normalizedSettings);
    } catch (error) {
      console.error('Failed to load payment settings:', error);
      showToast('Failed to load settings', 'red');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      generateQRCodeUrl();
    }
  }, [paymentSettings, loading]);

  const generateQRCodeUrl = () => {
    const upiUrl = `upi://pay?pa=${paymentSettings.upi_id}&pn=${encodeURIComponent(paymentSettings.upi_name)}&cu=INR`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(upiUrl)}`;
    setQrCodeUrl(qrUrl);
  };

  const showToast = (message: string, color: 'green' | 'blue' | 'red' = 'green') => {
    const successDiv = document.createElement('div');
    const bgColor = color === 'green' ? 'bg-green-500' : color === 'blue' ? 'bg-blue-500' : 'bg-red-500';
    successDiv.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50`;
    successDiv.innerHTML = `<i class="ri-check-line mr-2"></i>${message}`;
    document.body.appendChild(successDiv);
    setTimeout(() => {
      if (successDiv.parentElement) successDiv.parentElement.removeChild(successDiv);
    }, 3000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.updatePaymentSettings(tempSettings);
      setPaymentSettings(tempSettings);
      setIsEditing(false);
      showToast('Payment settings updated successfully!', 'green');
    } catch (error) {
      console.error('Failed to save payment settings:', error);
      showToast('Failed to save settings', 'red');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTempSettings(paymentSettings);
    setIsEditing(false);
  };

  const handleToggleChange = async (field: 'qr_code_enabled' | 'premium_fee_enabled' | 'whatsapp_enabled', value: boolean) => {
    try {
      const newSettings = { ...paymentSettings, [field]: value };
      await apiClient.updatePaymentSettings(newSettings);
      setPaymentSettings(newSettings);
      setTempSettings(newSettings);
      const fieldName = field === 'qr_code_enabled' ? 'QR Code' : field === 'premium_fee_enabled' ? 'Premium Fee' : 'WhatsApp';
      showToast(`${fieldName} ${value ? 'enabled' : 'disabled'}!`, 'blue');
    } catch (error) {
      console.error('Failed to update setting:', error);
      showToast('Failed to update setting', 'red');
    }
  };

  const testQRCode = () => {
    const testAmount = 100;
    const upiUrl = `upi://pay?pa=${paymentSettings.upi_id}&pn=${encodeURIComponent(paymentSettings.upi_name)}&am=${testAmount}&cu=INR&tn=Test%20Payment`;
    const testQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiUrl)}`;

    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-2xl p-6 max-w-sm mx-4">
        <h3 class="text-xl font-bold text-gray-900 mb-4 text-center">Test QR Code</h3>
        <div class="text-center">
          <img src="${testQrUrl}" alt="Test QR Code" class="mx-auto mb-4" />
          <p class="text-sm text-gray-600 mb-4">Test amount: ₹${testAmount}</p>
          <button id="closeTestQrBtn" class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg">
            Close
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    const closeBtn = document.getElementById('closeTestQrBtn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (modal.parentElement) modal.parentElement.removeChild(modal);
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <i className="ri-qr-code-line text-primary-400 mr-3"></i>
            Payment & QR Code Settings
          </h2>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-edit-line mr-2"></i>
                Edit Settings
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-save-line mr-2"></i>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-line mr-2"></i>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Form */}
          <div className="space-y-6">
            {/* UPI Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">UPI Payment Details</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">UPI ID</label>
                  <input
                    type="text"
                    value={isEditing ? tempSettings.upi_id : paymentSettings.upi_id}
                    onChange={(e) => setTempSettings(prev => ({ ...prev, upi_id: e.target.value }))}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-100 text-sm"
                    placeholder="Enter UPI ID (e.g., salon@upi)"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Business Name</label>
                  <input
                    type="text"
                    value={isEditing ? tempSettings.upi_name : paymentSettings.upi_name}
                    onChange={(e) => setTempSettings(prev => ({ ...prev, upi_name: e.target.value }))}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-100 text-sm"
                    placeholder="Enter business name"
                  />
                </div>
              </div>
            </div>

            {/* Premium Fee Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Premium Fee Settings</h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-700 font-semibold">Enable Premium Fee</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentSettings.premium_fee_enabled}
                      onChange={(e) => handleToggleChange('premium_fee_enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Premium Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    value={isEditing ? tempSettings.premium_amount : paymentSettings.premium_amount}
                    onChange={(e) => setTempSettings(prev => ({ ...prev, premium_amount: parseInt(e.target.value) || 0 }))}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-100 text-sm"
                    placeholder="Enter premium amount"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Premium fee added to all bookings for enhanced service
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">WhatsApp Settings</h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-gray-700 font-semibold">Enable WhatsApp</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentSettings.whatsapp_enabled}
                      onChange={(e) => handleToggleChange('whatsapp_enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">WhatsApp Number</label>
                  <input
                    type="text"
                    value={isEditing ? tempSettings.whatsapp_number : paymentSettings.whatsapp_number}
                    onChange={(e) => setTempSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-100 text-sm"
                    placeholder="Enter WhatsApp number with country code"
                  />
                </div>
              </div>
            </div>

            {/* QR Code Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code Settings</h3>

              <div className="flex items-center justify-between">
                <label className="text-gray-700 font-semibold">Enable QR Code Payments</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentSettings.qr_code_enabled}
                    onChange={(e) => handleToggleChange('qr_code_enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Test QR Code */}
            <div className="pt-4">
              <button
                onClick={testQRCode}
                disabled={!paymentSettings.qr_code_enabled}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center justify-center ${
                  paymentSettings.qr_code_enabled
                    ? 'bg-purple-500 hover:bg-purple-600 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <i className="ri-qr-scan-2-line mr-2"></i>
                Test QR Code
              </button>
            </div>
          </div>

          {/* QR Code Preview */}
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code Preview</h3>

              {paymentSettings.qr_code_enabled ? (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg inline-block shadow-sm">
                    <img
                      src={qrCodeUrl}
                      alt="Payment QR Code"
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                  <div className="text-sm text-gray-600">
                    <p className="font-semibold">UPI ID: {paymentSettings.upi_id}</p>
                    <p>Business: {paymentSettings.upi_name}</p>
                    <p className="mt-2 text-xs">
                      This QR code will be shown to customers for payments
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-12">
                  <i className="ri-qr-code-line text-gray-400 text-6xl mb-4"></i>
                  <p className="text-gray-500">QR Code payments are disabled</p>
                </div>
              )}
            </div>

            {/* Current Settings Summary */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-3">Current Settings</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-800">QR Code:</span>
                  <span className={`font-semibold ${paymentSettings.qr_code_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {paymentSettings.qr_code_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-800">Premium Fee:</span>
                  <span className={`font-semibold ${paymentSettings.premium_fee_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {paymentSettings.premium_fee_enabled ? `₹${paymentSettings.premium_amount}` : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-800">WhatsApp:</span>
                  <span className={`font-semibold ${paymentSettings.whatsapp_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {paymentSettings.whatsapp_enabled ? paymentSettings.whatsapp_number : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-800">UPI ID:</span>
                  <span className="font-semibold text-blue-900">{paymentSettings.upi_id}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettingsManager;
